import type {
  FeeRulePreviewRequest,
  FeeRulePreviewResponse,
  FeeScheduleCreateRequest,
  FeeScheduleResponse,
  FeeScheduleUpdateRequest,
  PlatformFeeRuleInput,
  PlatformFeeRuleRecord,
  PlatformFeeRuleRequest,
} from '../../types';
import type { RequestFn } from '../../client';

/**
 * Platform-wide fee schedule management. Admin-only routes under
 * `/admin/fees/*`. Exposed only via `'@delopay/sdk/internal'`.
 *
 * `platformFees.rules` manages the platform-owned Euclid fee-rule program per
 * merchant, which takes precedence over the flat platform schedules. Build the
 * program with `feeProgram()`.
 */
export class PlatformFees {
  /** Platform-owned Euclid fee-rule program (`/admin/fees/rules`). */
  readonly rules: PlatformFeeRulesManager;

  constructor(private readonly request: RequestFn) {
    this.rules = new PlatformFeeRulesManager(request);
  }

  /** Create a platform fee schedule for a specific merchant. */
  async create(params: FeeScheduleCreateRequest, merchantId: string): Promise<FeeScheduleResponse> {
    return this.request('POST', '/admin/fees', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /** List every platform fee schedule assigned to a merchant. */
  async list(merchantId: string): Promise<FeeScheduleResponse[]> {
    return this.request('GET', '/admin/fees/list', {
      query: { merchant_id: merchantId },
    });
  }

  /** Retrieve a single platform fee schedule by ID. */
  async retrieve(feeId: string): Promise<FeeScheduleResponse> {
    return this.request('GET', `/admin/fees/${encodeURIComponent(feeId)}`);
  }

  /** Update a platform fee schedule. */
  async update(feeId: string, params: FeeScheduleUpdateRequest): Promise<FeeScheduleResponse> {
    return this.request('PUT', `/admin/fees/${encodeURIComponent(feeId)}`, { body: params });
  }

  /** Delete a platform fee schedule. */
  async delete(feeId: string): Promise<FeeScheduleResponse> {
    return this.request('DELETE', `/admin/fees/${encodeURIComponent(feeId)}`);
  }
}

/**
 * Manages a merchant's platform-owned Euclid fee-rule programs (admin surface),
 * merchant-wide or per-shop (one active program per scope; a shop-scoped
 * program wins for its shop, else the merchant-wide one applies). Build the
 * `algorithm` with `feeProgram()`. The SDK injects `fee_owner: 'platform'`; set
 * `profile_id` on the input to scope a program to a shop.
 */
export class PlatformFeeRulesManager {
  constructor(private readonly request: RequestFn) {}

  /** Create or replace the platform fee-rule program for a merchant. */
  async upsert(params: PlatformFeeRuleInput, merchantId: string): Promise<PlatformFeeRuleRecord> {
    const body: PlatformFeeRuleRequest = { ...params, fee_owner: 'platform' };
    return this.request('PUT', '/admin/fees/rules', {
      body,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Retrieve the active platform fee-rule program for a scope, or `null`.
   * `profileId` omitted = merchant-wide program; set = that shop's program.
   */
  async retrieve(merchantId: string, profileId?: string): Promise<PlatformFeeRuleRecord | null> {
    return this.request('GET', '/admin/fees/rules', {
      query: { merchant_id: merchantId, profile_id: profileId },
    });
  }

  /**
   * Deactivate a platform fee-rule program. Idempotent. `profileId` omitted
   * targets the merchant-wide program; set targets only that shop's program.
   */
  async delete(merchantId: string, profileId?: string): Promise<void> {
    await this.request('DELETE', '/admin/fees/rules', {
      query: { merchant_id: merchantId, profile_id: profileId },
    });
  }

  /**
   * Dry-run a candidate fee-rule program against a sample transaction.
   * Returns the matched rule name, whether it fell through, and the computed fee.
   * Does not persist anything.
   */
  async preview(input: FeeRulePreviewRequest, merchantId: string): Promise<FeeRulePreviewResponse> {
    return this.request('POST', '/admin/fees/rules/preview', {
      body: input,
      query: { merchant_id: merchantId },
    });
  }
}
